import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSessionStorage } from '@spotify/confidence-core-react';

import { type AccountDetailsState, DEFAULT_STATE } from '../signupUtils';
import { useAuthConnections } from './useAuthConnections';
import type { SignupInvite } from './useInvite';
import { useWorkspaceNameFields } from './useWorkspaceNameFields';

type UseSignupAccountDetailsOptions = {
  inviteId?: string;
  invite?: SignupInvite;
  user?: { email?: string; sub?: string };
};

export const useSignupAccountDetails = ({
  inviteId,
  invite,
  user,
}: UseSignupAccountDetailsOptions) => {
  const [accountDetails, setAccountDetails] =
    useSessionStorage<AccountDetailsState>(
      `signup-slim-v1-${inviteId ?? 'regular'}`,
      DEFAULT_STATE,
      { migrateKey: false },
    );
  const [hasResetAccountDetails, setHasResetAccountDetails] = useState(false);

  const setField = useCallback(
    (newData: Partial<AccountDetailsState>) => {
      setAccountDetails(current => ({ ...current, ...newData }));
    },
    [setAccountDetails],
  );

  const hasInvite = invite !== undefined;
  const autoJoinDomain = invite?.autoJoinDomain;
  useEffect(() => {
    if (!hasInvite) return;
    setField({ loginDomain: autoJoinDomain || undefined });
  }, [hasInvite, autoJoinDomain, setField]);

  useEffect(() => {
    if (!user?.email) return;
    setField({ adminEmail: user.email });
  }, [user?.email, setField]);

  const {
    toggleAuthConnection: applyAuthConnectionToggle,
    hasSelectedAuthConnection,
    showAuthConnections,
    userAuthConnection,
    authConnections,
  } = useAuthConnections({
    user,
    inviteId,
    authConnections: accountDetails.authConnections,
    authConnectionsCustomized:
      accountDetails.authConnectionsCustomized || hasResetAccountDetails,
    setAccountDetails,
  });
  const resolvedAccountDetails = useMemo(
    () => ({ ...accountDetails, authConnections }),
    [accountDetails, authConnections],
  );

  const {
    handleDisplayNameChange: applyDisplayNameChange,
    handleDisplayNameBlur,
    validDisplayName,
    helperText,
  } = useWorkspaceNameFields({
    displayName: accountDetails.displayName,
    setField,
  });

  const [acceptMarketingEmails, setAcceptMarketingEmails] = useState(false);

  const resetAccountDetails = useCallback(() => {
    setHasResetAccountDetails(true);
    setAccountDetails({
      ...DEFAULT_STATE,
      authConnectionsCustomized: true,
    });
  }, [setAccountDetails]);

  return {
    accountDetails: resolvedAccountDetails,
    acceptMarketingEmails,
    setAcceptMarketingEmails,
    resetAccountDetails,
    setField,
    applyAuthConnectionToggle,
    hasSelectedAuthConnection,
    showAuthConnections,
    userAuthConnection,
    applyDisplayNameChange,
    handleDisplayNameBlur,
    validDisplayName,
    helperText,
  };
};
